import { Children, cloneElement, isValidElement, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTable as PrDataTable, type DataTableProps, type DataTableValueArray } from 'primereact/datatable'
import { type ColumnProps } from 'primereact/column'
import { Skeleton } from 'primereact/skeleton'

/** Tüm tablolarda tek tip paginator (satır/sayfa dropdown + "N kayıttan X-Y" raporu). */
// Kayıt bilgisi (CurrentPageReport) solda; gezinme butonları + satır/sayfa dropdown'ı sağda.
const PAGINATOR_TEMPLATE =
  'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown'

/** Yükleme sırasında gösterilecek iskelet satır sayısı. */
const SKELETON_ROWS = Array.from({ length: 6 }, (_, i) => ({ id: `__sk_${i}` }))

/**
 * Paylaşılan styled DataTable sarmalayıcı (anayasa: davranışsal bileşen PrimeReact styled + Reuse Mandate).
 * **Baz tasarım = Talep Listesi**: çizgisiz (zebra yok) + satır-hover + tek tip **paginator** (satır/sayfa
 * dropdown 5/10/20/50/100/200 + ortak sayfa raporu). Varsayılan sayfa boyutu 10'dur; çağıran `rows` ile
 * (ör. Talep Listesi 50) ve gerekirse diğer paginator prop'larıyla varsayılanı geçersiz kılabilir
 * (defaultlar {...props}'tan önce). Tüm ekranlar (bilet listesi, admin lookup'lar) aynı paginatörü buradan alır.
 *
 * **Yükleme:** `loading` iken PrimeReact loading overlay'i yerine, aynı kolon yapısıyla **iskelet satırlar**
 * (Skeleton) gösterilir — başlıklar korunur, her hücre bir Skeleton (paginator/etkileşim yok). Bitince
 * gerçek tablo render edilir.
 *
 * **Dil değişiminde tablo yeniden kurulur (`key`).** PrimeReact'in `BodyCell`'i `React.memo`'dur ve
 * karşılaştırıcısı **sabit bir anahtar listesine** bakar (`rowData`, `field`, `index`, `editing` …);
 * hücrenin `body` **render işlevi o listede YOKTUR**. Dile göre metin üreten bir `body`
 * (`lib/localizedText`) yalnız kendi closure'ını değiştirdiği için hücre bayat kalır: başlıklar
 * çevrilir, hücre eski dilde durur. Kullanıcı bunu 24.09.2026'da bildirdi ve belirti **tek yönlüydü**
 * — TR→EN çalışıyor görünüyordu, çünkü o geçiş sorguyu da tazeliyor ve tablo iskelet dalından geçerken
 * hücreler zaten sökülüyordu; dönüşte veri önbellekten geldiği için iskelet dalı hiç çalışmıyor ve
 * bayatlık ortaya çıkıyordu. `key` ile tablo dil başına bir kez kurulur (kalıcı bir render maliyeti
 * yok). `cellMemo={false}` de çözerdi ama memo'yu **her satırda sürekli** kapatırdı. Kontrollü
 * (`first`/`rows`/`sortField` prop'u verilen) tablolarda yeniden kurulum durumu kaybetmez; kontrolsüz
 * tablolarda sayfa 1'e döner — dil değiştirmenin nadirliği karşısında kabul edildi.
 * Bariyer: `localizedColumnLanguageSwitch.test.tsx`.
 */
export default function DataTable<TValue extends DataTableValueArray>(props: DataTableProps<TValue>) {
  const { t, i18n } = useTranslation()
  const { emptyMessage, loading, children, ...rest } = props

  // Yükleme: kolon yapısını koruyup her hücreyi Skeleton yapan iskelet tablo.
  if (loading) {
    const skeletonCols = Children.map(children, (child) =>
      isValidElement(child)
        ? cloneElement(child as ReactElement<ColumnProps>, { body: () => <Skeleton />, sortable: false })
        : child,
    )
    return (
      // `ui-table-scroll`: mobil taşma güvenlik ağı — bkz. aşağıdaki gerçek tablo dönüşündeki not.
      <div className="ui-table-scroll">
        <PrDataTable
          value={SKELETON_ROWS as unknown as TValue}
          dataKey="id"
          size="small"
          aria-busy
        >
          {skeletonCols}
        </PrDataTable>
      </div>
    )
  }

  return (
    // `ui-table-scroll` (ui.css): telefon genişliğinde kolonları sıkıştırmak yerine tabloyu KENDİ
    // içinde yatay kaydırır — sayfanın tamamı değil. `tk-table-fit`/`admin-table-wrap` (table-layout:
    // fixed + ellipsis) kullanan tablolarda zaten sığdığı için no-op'tur; onu KULLANMAYAN tablolarda
    // (çoğunluk) daha önce sayfayı yana kaydıran taşmayı tabloya hapseder (Reuse Mandate — tek yerden
    // tüm tablolara).
    <div className="ui-table-scroll">
      <PrDataTable
        key={i18n.language}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
        paginatorTemplate={PAGINATOR_TEMPLATE}
        currentPageReportTemplate={t('common.pageReport')}
        size="small"
        rowHover
        removableSort
        dataKey="id"
        {...rest}
        emptyMessage={emptyMessage}
      >
        {children}
      </PrDataTable>
    </div>
  )
}
